<template>
  <div>
    <LoadingWrapper :state="status">
      <div class="flex flex-row items-center gap-1">
        <BaseHorizontalOverflow class="-ml-4 grow">
          <div class="my-1 w-fit px-4">
            <BaseButtonGroup>
              <BaseTextButton
                v-for="feedbackTopic in data.feedbackTopics"
                :key="feedbackTopic.id"
                color="tonal"
                :icon="feedbackTopic.icon"
                :selected="selectedFeedbackTopicId === feedbackTopic.id"
                @click="selectedFeedbackTopicId = feedbackTopic.id"
              >
                {{ feedbackTopic.title }}
              </BaseTextButton>
            </BaseButtonGroup>
          </div>
        </BaseHorizontalOverflow>
        <BaseIconButton
          v-tooltip.bottom="$t('profile.download.tooltip')"
          icon="download"
          size="M"
          width="narrow"
          color="outlined"
          :loading="isDownloading"
          @click="openExportDialog"
        />
      </div>
      <div v-if="selectedFeedbackTopic">
        <CoacheeJournalProfile
          v-if="selectedFeedbackTopic.type === FeedbackTopicType.Journal"
          :key="selectedFeedbackTopic.id"
          :feedback-topic-id="selectedFeedbackTopic.id"
        />
        <CoacheeEvaluationProfile
          v-if="selectedFeedbackTopic.type === FeedbackTopicType.Evaluation"
          :key="selectedFeedbackTopic.id"
          :feedback-topic-id="selectedFeedbackTopic.id"
        />
      </div>
      <CoacheeProfileExportDialog
        :is-open="isExportDialogOpen"
        @close="closeExportDialog"
        @submit="handleCreateExportPayload"
      />
    </LoadingWrapper>
  </div>
</template>

<script lang="ts" setup>
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useCoacheeProfile } from '../composables/useCoacheeProfile'
  import { ref, watch, computed } from 'vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { FeedbackTopicType } from '@/core/models/feedback-topic'
  import CoacheeJournalProfile from '../components/CoacheeJournalProfile.vue'
  import CoacheeEvaluationProfile from '../components/CoacheeEvaluationProfile.vue'
  import BaseButtonGroup from '@/core/components/base/button/BaseButtonGroup.vue'
  import BaseHorizontalOverflow from '@/core/components/base/horizontal-overflow/BaseHorizontalOverflow.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import CoacheeProfileExportDialog from '../components/CoacheeProfileExportDialog.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import { useFileDownload } from '@/core/composables/use-download-file'
  import { FetchMethod } from '@/core/models/api/fetch-method'
  import { useRoute } from 'vue-router'
  import type { CoacheeProfilePdfPayload } from '../composables/use-coachee-profile-export-options'

  const { data, status } = useCoacheeProfile()
  const selectedFeedbackTopicId = ref<string | null>(null)
  const route = useRoute()
  const coacheeId = (route.params.coacheeId ?? route.params.id) as string

  const {
    isOpen: isExportDialogOpen,
    openDialog: openExportDialog,
    closeDialog: closeExportDialog,
  } = useDialog()
  const { downloadFile, isDownloading } = useFileDownload(
    `coaching/coachee/${coacheeId}/profile/pdf`,
  )

  watch(data, () => {
    selectedFeedbackTopicId.value = data.value?.feedbackTopics[0]?.id ?? null
  })

  const selectedFeedbackTopic = computed(() => {
    return (
      data.value?.feedbackTopics.find(
        (feedbackTopic) => feedbackTopic.id === selectedFeedbackTopicId.value,
      ) ?? null
    )
  })

  const handleCreateExportPayload = (payload: CoacheeProfilePdfPayload) => {
    downloadFile({
      method: FetchMethod.POST,
      body: payload,
    })
  }
</script>
